import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { ExternalLink, ShieldCheck, CreditCard } from "lucide-react";

const JOTFORM_EMBED_URL =
    "https://form.jotform.com/bennebrands/pressrelease";
const JOTFORM_FORM_ID = "pressrelease";

export default function SubmitPressReleasePage() {
    const iframeRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(2400);

    // Listen for JotForm's resize events so the iframe auto-fits its content
    // across the multi-step form, including the file-upload and payment steps.
    useEffect(() => {
        const onMessage = (e) => {
            if (
                typeof e.data !== "string" ||
                !e.origin.includes("jotform.com")
            ) {
                return;
            }
            // JotForm posts strings like "setHeight:2456:242886688552172"
            const parts = e.data.split(":");
            if (parts[0] === "setHeight" && parts.length >= 2) {
                const h = parseInt(parts[1], 10);
                if (!Number.isNaN(h) && h > 400) setIframeHeight(h);
            }
            // Scroll-to-top hint from JotForm when a step changes
            if (parts[0] === "scrollIntoView") {
                iframeRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    return (
        <div data-testid="submit-press-release-page">
            <PageHero
                eyebrow="For PR & comms teams"
                title="Submit a Press Release"
                lead="Pitch your opening, award, new hire, launch, pop-up, event, fundraiser or obituary. Choose Standard, Enhanced or Premium — pay securely with Stripe at the end of the form."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Submit a Press Release" },
                ]}
            />

            {/* Trust strip */}
            <div className="bg-[#FDF9EF] border-b border-black/5">
                <div className="px-4 md:px-8 lg:px-12 py-5 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
                    <div className="flex items-start gap-3" data-testid="trust-stripe">
                        <CreditCard className="h-5 w-5 text-[#E63B26] mt-0.5 shrink-0" />
                        <div>
                            <p className="font-poppins font-semibold text-sm">
                                Stripe-secured payment
                            </p>
                            <p className="font-sofia text-xs text-[#1B1B1B]/70">
                                Cards are processed by Stripe. We never see your
                                card number.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3" data-testid="trust-review">
                        <ShieldCheck className="h-5 w-5 text-[#E63B26] mt-0.5 shrink-0" />
                        <div>
                            <p className="font-poppins font-semibold text-sm">
                                Reviewed within 48h
                            </p>
                            <p className="font-sofia text-xs text-[#1B1B1B]/70">
                                Expedited posts ($75 add-on) go live in 1–5
                                business hours.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3" data-testid="trust-pricing">
                        <a
                            href="/submit-press-release/billing"
                            className="flex items-start gap-3 hover:text-[#E63B26] transition"
                        >
                            <ExternalLink className="h-5 w-5 text-[#E63B26] mt-0.5 shrink-0" />
                            <div>
                                <p className="font-poppins font-semibold text-sm">
                                    Standard $150 · Enhanced $225 · Premium $275
                                </p>
                                <p className="font-sofia text-xs text-[#1B1B1B]/70">
                                    See tier breakdown on the{" "}
                                    <span className="underline">Billing</span>{" "}
                                    page.
                                </p>
                            </div>
                        </a>
                    </div>
                </div>
            </div>

            {/* JotForm embed — preserves the user's existing form + Stripe flow */}
            <div className="px-4 md:px-8 lg:px-12 py-10">
                <div
                    className="bg-white border border-black/10 overflow-hidden"
                    data-testid="jotform-embed-wrap"
                >
                    <iframe
                        ref={iframeRef}
                        id={`JotFormIFrame-${JOTFORM_FORM_ID}`}
                        title="Press Release / Promotion Submission"
                        src={JOTFORM_EMBED_URL}
                        allowTransparency="true"
                        allow="geolocation; microphone; camera; payment"
                        allowFullScreen
                        scrolling="no"
                        className="w-full block"
                        style={{
                            border: "0",
                            minHeight: "780px",
                            height: `${iframeHeight}px`,
                            background: "#ffffff",
                        }}
                        data-testid="jotform-iframe"
                    />
                </div>

                <p className="font-sofia text-xs text-[#1B1B1B]/60 mt-3 text-center">
                    Having trouble? Open the form in a new tab:{" "}
                    <a
                        href={JOTFORM_EMBED_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#E63B26] underline"
                        data-testid="jotform-direct-link"
                    >
                        form.jotform.com/bennebrands/pressrelease
                    </a>{" "}
                    · Questions about pricing? See the{" "}
                    <Link
                        to="/submit-press-release/faqs"
                        className="text-[#E63B26] underline"
                    >
                        FAQs
                    </Link>
                    .
                </p>
            </div>
        </div>
    );
}
